import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart, money } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 } }),
};

export default function ProductDetail() {
  const { id } = useParams(); // handle
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState({});
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    setProduct(null);
    setNotFound(false);
    setSelected({});
    setActiveImg(0);
    axios
      .get(`${API}/shop/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // preselect options that have a single value
        const pre = {};
        (r.data.options || []).forEach((o) => {
          if (o.values.length === 1) pre[o.name] = o.values[0];
        });
        setSelected(pre);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error("Could not load this product");
      });
  }, [id]);

  const activeVariant = useMemo(() => {
    if (!product) return null;
    const opts = product.options || [];
    if (opts.some((o) => !selected[o.name])) return null;
    return product.variants.find((v) => v.selectedOptions.every((so) => selected[so.name] === so.value)) || null;
  }, [product, selected]);

  const price = activeVariant ? activeVariant.price : product?.minPrice;

  const addToBag = () => {
    const opts = product.options || [];
    const missing = opts.find((o) => !selected[o.name]);
    if (missing) {
      toast.error(`Select ${missing.name.toLowerCase()} first.`);
      return;
    }
    if (!activeVariant) {
      toast.error("That combination is unavailable.");
      return;
    }
    if (!activeVariant.available) {
      toast.error("That option is sold out.");
      return;
    }
    addItem({
      variantId: activeVariant.id,
      productTitle: product.title,
      variantTitle: activeVariant.title,
      price: activeVariant.price,
      currency: activeVariant.currency,
      image: activeVariant.image || product.image,
      handle: product.handle,
    });
    toast.success("Added to your pack", { description: `${product.title} · ${activeVariant.title}` });
    setOpen(true);
  };

  const images = product?.images?.length ? product.images : product?.image ? [product.image] : [];

  // Shopify/Tapstitch bakes light-theme inline styles + fixed widths into the description
  // HTML (esp. the size table). Strip them so our dark-theme CSS controls the look.
  const cleanHtml = useMemo(() => {
    if (!product) return "";
    const raw = product.descriptionHtml || (product.description ? `<p>${product.description}</p>` : "");
    if (!raw) return "";
    try {
      const doc = new DOMParser().parseFromString(raw, "text/html");
      doc.querySelectorAll("*").forEach((el) => {
        el.removeAttribute("style");
        el.removeAttribute("width");
        el.removeAttribute("height");
        el.removeAttribute("bgcolor");
        el.removeAttribute("align");
        el.removeAttribute("id");
        el.removeAttribute("class");
      });
      return doc.body.innerHTML;
    } catch {
      return raw;
    }
  }, [product]);

  return (
    <div className="bg-[#050505] min-h-screen grain" data-testid="product-detail">
      <Navbar />
      <main className="mx-auto max-w-[1600px] px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <button
          data-testid="back-home"
          onClick={() => navigate("/")}
          className="group flex items-center gap-2 font-sans text-xs tracking-[0.2em] text-white/50 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
          BACK TO THE ARSENAL
        </button>

        {notFound && (
          <div className="py-32 text-center">
            <h1 className="font-display font-black uppercase text-white text-5xl tracking-normal">Not found</h1>
            <p className="font-sans text-white/50 mt-4">This piece doesn't exist. Yet.</p>
          </div>
        )}

        {product && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Gallery */}
            <motion.div variants={fade} initial="hidden" animate="show" className="flex flex-col gap-4">
              <div className="relative bg-[#0b0b0b] border border-white/10 overflow-hidden min-h-[440px] lg:min-h-[600px] flex items-center justify-center">
                <div className="absolute inset-0 spotlight pointer-events-none" />
                {images[activeImg] && (
                  <img data-testid="product-image" src={images[activeImg]} alt={product.title} className="relative z-[1] max-h-[420px] lg:max-h-[560px] w-full object-contain px-6" />
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-3 flex-wrap">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImg(idx)}
                      className={`w-16 h-20 border overflow-hidden transition-colors ${activeImg === idx ? "border-white" : "border-white/15 hover:border-white/40"}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details */}
            <div className="flex flex-col justify-center">
              <motion.span variants={fade} custom={0} initial="hidden" animate="show" className="font-sans text-[11px] tracking-[0.45em] text-white/40">
                {product.tags?.[0] || "LONE WOLF GYM GEAR"}
              </motion.span>
              <motion.h1 variants={fade} custom={1} initial="hidden" animate="show" data-testid="product-title" className="mt-4 font-display font-black uppercase tracking-normal leading-[0.95] text-white text-4xl md:text-6xl">
                {product.title}
              </motion.h1>
              <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="mt-5 flex items-center gap-4">
                <span data-testid="product-price" className="font-display font-bold text-3xl text-white">{money(price, product.currency)}</span>
                <span className="h-4 w-px bg-white/20" />
                <span className="font-sans text-xs tracking-[0.15em] text-white/40">Made on demand · ships worldwide</span>
              </motion.div>

              {/* Option selectors */}
              <motion.div variants={fade} custom={4} initial="hidden" animate="show" className="mt-9 space-y-6">
                {(product.options || []).map((opt) => (
                  <div key={opt.name}>
                    <span className="font-sans text-[11px] tracking-[0.3em] text-white/50">{opt.name.toUpperCase()}</span>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {orderValues(opt).map((val) => {
                        const active = selected[opt.name] === val;
                        return (
                          <button
                            key={val}
                            data-testid={`option-${opt.name}-${val}`}
                            onClick={() => setSelected((s) => ({ ...s, [opt.name]: val }))}
                            className={`min-w-12 h-12 px-4 border font-sans text-sm tracking-wide transition-all duration-300 ${
                              active ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:border-white/60"
                            }`}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Add to bag */}
              <motion.div variants={fade} custom={5} initial="hidden" animate="show" className="mt-10">
                <button
                  data-testid="add-to-bag"
                  onClick={addToBag}
                  className="group relative w-full sm:w-auto sm:min-w-[320px] h-14 overflow-hidden border border-white bg-white text-black flex items-center justify-center gap-3 px-10"
                >
                  <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                  <ShoppingBag className="relative w-4 h-4 text-black group-hover:text-white transition-colors duration-500" />
                  <span className="relative font-sans text-xs tracking-[0.28em] text-black group-hover:text-white transition-colors duration-500">
                    ADD TO BAG — {money(price, product.currency)}
                  </span>
                </button>
                <p className="font-sans text-[11px] tracking-[0.1em] text-white/30 mt-4">Secure checkout via Shopify · Fulfilled by Tapstitch</p>
              </motion.div>
            </div>
          </div>

          {(product.descriptionHtml || product.description) && (
            <motion.section
              data-testid="product-details"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-20 lg:mt-28 max-w-4xl"
            >
              <div className="flex items-center gap-4 mb-8">
                <span className="font-sans text-[11px] tracking-[0.4em] text-white/40">DETAILS &amp; SIZING</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <div
                className="product-desc"
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
              />
            </motion.section>
          )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "XXL", "3XL", "XXXL", "4XL", "5XL"];
function orderValues(opt) {
  if (!/size/i.test(opt.name)) return opt.values;
  return [...opt.values].sort((a, b) => {
    const ia = SIZE_ORDER.indexOf(a.toUpperCase());
    const ib = SIZE_ORDER.indexOf(b.toUpperCase());
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}
